'use client';

import type { ComponentPropsWithoutRef, ForwardedRef } from 'react';

import type { DataAttributesProps } from '../../../utils/types';
import type { SelectOption } from '../common';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { forwardRef, useEffect, useState } from 'react';

import { Button, Popover, PopoverContent, PopoverTrigger } from '../../../ui';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '../../../ui/command';
import { Icon } from '../../icons';

/**
 * Props for the Combobox component.
 *
 * Multi-select combobox with badge display for selected items.
 * ARIA attributes are provided by Radix Popover. Selected items are shown as
 * removable badges. Includes a sr-only "Remove all" button when items are selected.
 */
type ComboboxProps = DataAttributesProps &
  Omit<
    ComponentPropsWithoutRef<'button'>,
    'onChange' | 'role' | 'type' | 'value'
  > & {
    /** Message shown when no options match the search query. */
    emptyLabel?: string;
    /** Enables a search input inside the dropdown to filter options. */
    hasSearch?: boolean;
    name?: string;
    /** Called with the full array of currently selected values whenever the selection changes. */
    onChange: (selectedValues: string[]) => void;
    /** Available options to select from. */
    options: SelectOption[];
    /**
     * Shown on the closed trigger when nothing is selected, and used as the
     * search input placeholder. When omitted, the empty trigger stays blank and
     * the search input falls back to "Rechercher".
     */
    placeholder?: string;
    /** Controlled array of selected values. */
    value?: string[];
  };

export const Combobox = forwardRef(
  (
    {
      className,
      'data-testid': dataTestId,
      emptyLabel = "Pas d'éléments trouvés",
      hasSearch,
      name,
      onChange,
      options,
      placeholder,
      value = [],
      ...rest
    }: ComboboxProps,
    ref: ForwardedRef<HTMLButtonElement>
  ) => {
    const testId = dataTestId ?? name;

    const [open, setOpen] = useState(false);
    const [selectedValues, setSelectedValues] = useState<string[]>(value);

    // Keep the internal selection in sync when the controlled `value` prop is
    // changed from the outside (e.g. a programmatic form.setValue during a CSV
    // import or when loading an existing record). Without this, the badges only
    // ever reflect the value present on mount and ignore later updates. The
    // content check avoids redundant re-renders when a parent passes a fresh
    // array reference with identical contents on every render.
    useEffect(() => {
      setSelectedValues((prev) =>
        prev.length === value.length && prev.every((v, i) => v === value[i])
          ? prev
          : value
      );
    }, [value]);

    const updateSelection = (newValues: string[]) => {
      setSelectedValues(newValues);
      onChange(newValues);
    };

    const handleSelect = (currentValue: string) => {
      const isValueSelected = selectedValues.includes(currentValue);
      const getNewValues = () => {
        if (isValueSelected) {
          return selectedValues.filter((v) => v !== currentValue);
        }
        return [...selectedValues, currentValue];
      };

      const newValues = getNewValues();

      updateSelection(newValues);

      if (!isValueSelected && newValues.length === 0) {
        setOpen(false);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      updateSelection([]);
      setOpen(false);
    };

    return (
      <div className="relative">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              ref={ref}
              {...rest}
              aria-expanded={open}
              className={cn(
                'flex min-h-10 w-full items-center rounded-md border border-input bg-background text-base ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
                className
              )}
              data-testid={testId}
              role="combobox"
              type="button"
            >
              <div className="flex flex-1 flex-wrap gap-1.5 px-1.5 py-1">
                {selectedValues.length === 0 && !!placeholder && (
                  <span className="px-1.5 py-1 text-muted-foreground">
                    {placeholder}
                  </span>
                )}
                {selectedValues.map((value) => {
                  const item = options.find((item) => item.value === value);
                  if (!item) {
                    return null;
                  }

                  return (
                    <Button
                      key={value}
                      className="focus-visible:ring-1 lg:h-7"
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelect(value);
                      }}
                    >
                      {item.label} <Icon name="x" />
                    </Button>
                  );
                })}
              </div>
              <div className="flex h-9 shrink-0 items-center gap-2 px-2 py-1">
                {selectedValues.length > 0 && (
                  <Button
                    className="size-8 h-full px-0 py-1"
                    size="sm"
                    title="Tout supprimer"
                    variant="ghost"
                    onClick={handleClear}
                  >
                    <span className="sr-only">Tout supprimer</span>
                    <Icon name="x" />
                  </Button>
                )}
                <Icon name="chevrons-up-down" />
              </div>
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0 lg:w-[--radix-popover-trigger-width]">
            <Command className="w-full min-w-80">
              {!!hasSearch && (
                <CommandInput
                  className="h-10"
                  placeholder={placeholder || 'Rechercher'}
                />
              )}
              <CommandList>
                <CommandEmpty>{emptyLabel}</CommandEmpty>
                <CommandGroup>
                  {options.map((item) => (
                    <CommandItem
                      key={item.value}
                      className="flex items-center space-x-2"
                      data-testid={`${testId}-${item.value}`}
                      tabIndex={0}
                      value={item.label}
                      onSelect={() => handleSelect(item.value)}
                    >
                      <span className="truncate">{item.label}</span>
                      {selectedValues.includes(item.value) && (
                        <Icon name="check" size="xs" />
                      )}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
    );
  }
);

Combobox.displayName = 'Combobox';
